import { Base64 } from 'js-base64'
import { CryptoJS, rsa, hexToBase64 } from './lib'
import { REGION } from '@ms/config/app'
import axios from './axios.js'

const TRANS_ID_KEY = `${REGION}_transId`

// 注意单页面开启多个标签页时, localCipher 可能不一致，所以都从 storage 取值吧
const getLocalCipher = () => {
  let localCipher = localStorage.getItem(TRANS_ID_KEY)
  if (localCipher) {
    try {
      localCipher = JSON.parse(localCipher)
      const { key, transId, date } = localCipher
      if (key && transId && date && date > Date.now()) {
        // 秘钥依然有效
        const parsedKey = CryptoJS.enc.Base64.parse(decodeURIComponent(key))
        return { key: parsedKey, transId, date }
      }
    } catch (error) {
      console.error(error)
    }
  }
  return null
}

const crypto = {
  // 缓存的服务器 cipher
  remoteCipher: null,

  removeLocalCipher(type) {
    if (type === 1) {
      this.remoteCipher = null
    } else if (type === 2) {
      localStorage.removeItem(TRANS_ID_KEY)
    }
  },

  /**
   * 加密请求
   * @param {String} data            请求路径
   * @param {String|Object} options  请求参数，支持 options.body, 默认直接传 body 即可
   * @param {String} type            加密类型
   */
  async fetchData(url = '', options = {}, type, { self = true, headers = {} } = {}) {
    let reqBody
    if (typeof options === 'string') {
      reqBody = options
    } else if (options.body) {
      reqBody = JSON.stringify(options.body)
    } else {
      reqBody = JSON.stringify(options)
    }
    console.log('crypto fetch request: ', url, options, type, headers)
    const { body, headers: encryptoHeaders } = await this.encrypt(reqBody, type)
    try {
      const res = await axios.post(url, body, { headers: { ...headers, ...encryptoHeaders }, self: true })
      const strData = await this.decrypt(res, type)
      const data = JSON.parse(strData)
      console.log('crypto fetch response: ', url, data)
      if (self) {
        return data
      }
      if (data.bizCode === 10000) {
        return data.data
      } else {
        const error = new Error(data.message)
        error.bizCode = data.bizCode
        return Promise.reject(error)
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        this.removeLocalCipher(type)
      }
      const newError = new Error('Sorry, something went wrong. Please try again.')
      newError.canceled = error.canceled
      newError.login = error.login
      return Promise.reject(newError)
    }
  },

  /**
   * 接口加密
   * @param {Object} data 加密参数
   * @param {Number} type 加密类型
   */
  async encrypt(data, type) {
    switch (type) {
      case 1: {
        // 通过后台获取一个aes的加密秘钥
        const { did, key } = await this.getAesCipher()
        const body = this.encryptionByAes(data, key)
        localStorage.setItem('deviceId', did)
        return { body, headers: { deviceId: did, did } }
      }
      case 2: {
        // 自己生成一个aes的秘钥通过 rsa上传到服务器，后期通过这个aes秘钥加密数据
        const { key, transId } = await this.createAesCipher()
        const body = this.encryptionByAes(data, key)
        return { body, headers: { transId } }
      }
      default:
        return { body: data }
    }
  },

  /**
   * 解密
   * @param {Object} data 加密参数
   * @param {Number} type 加密类型
   */
  async decrypt(data, type) {
    if (type === 1) {
      const { key } = await this.getAesCipher()
      // 解密数据
      return this.decryptionByAes(key, data)
    } else if (type === 2) {
      const { key } = await this.createAesCipher()
      // 解密数据
      return this.decryptionByAes(key, data)
    }
    console.error(new Error('Not found valid cipher type'))
    return data
  },

  // 根据aesKey加密 body，并加入需要 header
  encryptionByAes(body, aesKey) {
    const iv = CryptoJS.lib.WordArray.random(16)
    const encrypted = CryptoJS.AES.encrypt(body, aesKey, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    })
    body = iv.clone().concat(encrypted.ciphertext).toString(CryptoJS.enc.Base64)
    return body
  },

  // 解密aes加密的数据
  decryptionByAes(key, data) {
    try {
      const d = Base64.atob(data)
      const res = Base64.btoa(d.slice(16))
      const iv = CryptoJS.enc.Base64.parse(Base64.btoa(d.slice(0, 16)))
      // 返回数据带偏移量，直接移除偏移量
      const decrypted = CryptoJS.AES.decrypt(res, key, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      })
      return decrypted.toString(CryptoJS.enc.Utf8)
    } catch (e) {
      console.error(e)
    }
    return ''
  },

  // 加载即去获取cipher-- 单aes加密用
  // 单aes加密流程，先向后台请求一个aes机密的 秘钥，然后通过秘钥加密数据发给后台，秘钥一直保存，后期数据返回解密也通过这个秘钥
  async getAesCipher() {
    // 有些使用缓存的 cipher
    if (this.remoteCipher) {
      return this.remoteCipher
    }

    const data = await axios.post('/patron/cipher')
    const key = CryptoJS.enc.Base64.parse(data.symmetricCipher)
    const cipher = { did: data.did, key }
    this.remoteCipher = cipher
    return cipher
  },

  // 自己创建一个aes cipher 并发送给后台后面加密,都用这个cipher 当请求地址是2的时候走这个流程
  async createAesCipher() {
    // 缓存的秘钥依然有效
    const localCipher = getLocalCipher()
    if (localCipher && localCipher.date > Date.now()) {
      return localCipher
    }

    // 生成一个aes加密的key
    const key = CryptoJS.lib.WordArray.random(16)
    const cipher = key.toString(CryptoJS.enc.Base64)
    // 将key转成base64，并用aes加密后的hex转成base64，传递给后台
    const body = hexToBase64(rsa.encrypt(`password=${encodeURIComponent(cipher)}`))
    const data = await axios({
      url: '/base/cipher',
      method: 'post',
      headers: { 'Content-type': 'text/plain' },
      data: body
    })
    if (data) {
      // 秘钥保存在本地，防止中间盗窃,秘钥一个小时内有效，过期重新请求
      const cipherInfo = {
        transId: data.transId,
        key,
        date: Date.now() + 3600 * 1000
      }
      const value = {
        ...cipherInfo,
        key: encodeURIComponent(key.toString(CryptoJS.enc.Base64))
      }
      localStorage.setItem(TRANS_ID_KEY, JSON.stringify(value))
      return cipherInfo
    }
  }
}

/**
 * 加密请求
 * @param {String} url            请求路径
 * @param {String|Object} options  请求参数，支持 options.body, 默认直接传 body 即可
 * @param {String} type            加密类型, 支持 1, 2
 */
export const cryptoFetch = (...args) => crypto.fetchData(...args)

export default crypto
